<template>
  <b-sidebar
    id="sidebar"
    :sidebar-class="sidebarClass"
    no-header
    bg-variant="-"
    text-variant="-"
  >
    <b-nav vertical>
      <template v-for="(menu, $idx) in menuOptions">
        <SidebarNavItem
          v-if="!menu.children && (!menu.condition || menu.condition())"
          :key="$idx"
          :route="!!menu.click ? null : menu.route"
          :menu-text="menu.menuText"
          :menu-icon="menu.menuIcon"
          :href="menu.href"
          :exact="menu.exact"
          :badge="menu.badge"
          :target="menu.target"
          :item-class="menu.itemClass"
          @click=";(menu.click && menu.click()) || noop()"
        />

        <SidebarNavDropdown
          v-if="menu.children && menu.children.length && (!menu.condition || menu.condition())"
          :key="$idx"
          :menu-text="menu.menuText"
          :menu-icon="menu.menuIcon"
          :children="menu.children"
          :route="menu.route.name"
        />
      </template>
    </b-nav>
  </b-sidebar>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import logoutMixin from '@/mixins/logout.mixin'
import SidebarNavDropdown from './SidebarNavDropdown.vue'
import SidebarNavItem from './SidebarNavItem.vue'
import { isRingbaIntegrationEnabled } from '@/services/featureFlags'

export default {
  name: 'TheSidebar',

  components: {
    SidebarNavDropdown,
    SidebarNavItem
  },

  mixins: [logoutMixin],

  data() {
    return {
      menuOptions: [
        {
          menuText: 'Impersonate',
          menuIcon: ['fas', 'user-circle'],
          route: {
            name: 'AdminSwitchAccount'
          },
          condition: () => this.isSuperAdmin
        },
        {
          menuText: 'Dashboard',
          menuIcon: ['fas', 'tachometer-alt'],
          route: {
            name: 'SellerDashboard'
          },
          condition: () => false, // this.isSellerAccount,
          exact: true,
          badge: 'NEW'
        },
        {
          menuText: 'Call Logs',
          menuIcon: ['fas', 'phone-volume'],
          route: {
            name: 'SellerCallLogs'
          },
          condition: () => this.isSellerAccount
        },
        {
          menuText: 'Call Logs',
          menuIcon: ['fas', 'phone-volume'],
          route: {
            name: 'BuyerCallLogs'
          },
          condition: () => this.isBuyerAccount
        },
        {
          menuText: 'Campaigns',
          menuIcon: ['fas', 'chart-line'],
          condition: () => this.isBuyerAccount,
          route: {
            name: 'ListBuyerCampaigns|BuyerCreateCampaign'
          },
          children: [
            {
              subMenuText: 'Campaigns',
              route: {
                name: 'ListBuyerCampaigns'
              },
            },
            {
              subMenuText: 'Create Campaign',
              route: {
                name: 'BuyerCreateCampaign'
              },
            }
          ]
        },
        {
          menuText: 'Offers',
          menuIcon: ['fas', 'chart-line'],
          route: {
            name: 'SellerAvailableOffers'
          },
          condition: () => this.isSellerAccount
        },
        {
          menuText: 'Creatives',
          menuIcon: ['fas', 'photo-video'],
          route: {
            name: 'SellerCreatives'
          },          
          condition: () => this.isSellerAccount
        },
        {
          menuText: 'Creatives',
          menuIcon: ['fas', 'photo-video'],
          route: {
            name: 'ReviewCreatives'
          },          
          condition: () => this.isBuyerAccount && this.hasAllowedMedicareCategory
        },

        {
          menuText: 'Users',
          menuIcon: ['fas', 'users'],
          route: {
            name: 'UserManagement'
          },          
        },

        {
          menuText: 'Settings',
          menuIcon: ['fas', 'cog'],
          route: {
            name: 'BuyerAccountBudget|BuyerCampaignSettings|AccountTimezone'
          },
          children: [
            {
              subMenuText: 'Account Budget',
              route: {
                name: 'BuyerAccountBudget'
              },
              condition: () => this.isBuyerAccount && this.accountDetails?.budget?.dailyBudgetInUSD
            },

            {
              subMenuText: 'Campaigns',
              route: {
                name: 'BuyerCampaignSettings'
              },
              condition: () => this.isBuyerAccount
            },

            {
              subMenuText: 'Timezone',
              route: {
                name: 'AccountTimezone'
              }
            },
          ]
        },

        {
          menuText: 'Security',
          menuIcon: ['fas', 'key'],
          route: {
            name: 'TwoFactorAuth|ListApiTokens|CreateApiToken'
          },
          children: [
            {
              subMenuText: 'Multi-Factor Auth',
              route: {
                name: 'TwoFactorAuth'
              },
              'data-cy': 'button-2fa'
            },
            {
              subMenuText: 'API Tokens',
              route: {
                name: 'ListApiTokens'
              },
              'data-cy': 'button-api-tokens'
            },
          ]
        },

        {
          menuText: 'Billing',
          menuIcon: ['fas', 'credit-card'],
          route: {
            name: 'BillingSettings|BankInfo|AddFunds|AddCreditCard|PaymentMethods|MyAccount'
          },
          children: [
            {
              subMenuText: 'My Account',
              route: {
                name: 'MyAccount'
              },
              'data-cy': 'button-my-account',
              badge: 'NEW'
            },
            {
              subMenuText: 'Payment Methods',
              route: {
                name: 'PaymentMethods'
              },
              'data-cy': 'button-payment-methods',
              condition: () => this.isBuyerAccount,
            },
            {
              subMenuText: 'Add Credit Card',
              route: {
                name: 'AddCreditCard'
              },
              'data-cy': 'button-add-credit-card',
              condition: () => this.isBuyerAccount,
            },
            {
              subMenuText: 'Add Funds',
              route: {
                name: 'AddFunds'
              },
              'data-cy': 'button-add-funds',
              condition: () => this.isBuyerAccount,
            },
            {
              subMenuText: 'Advance Pay',
              route: {
                name: 'AdvancePay'
              },
              'data-cy': 'button-add-funds',
              condition: () => this.isBuyerAccount,
            },
            {
              subMenuText: 'Bank Info',
              route: {
                name: 'BankInfo'
              },
              'data-cy': 'button-bank-info',
              condition: () => this.isSellerAccount,
            },
            {
              subMenuText: 'Settings',
              route: {
                name: 'BillingSettings'
              },
              'data-cy': 'button-billing-settings',
              condition: () => this.isBuyerAccount,
            },
          ]
        },
        {
          menuText: 'Help & Resources',
          menuIcon: ['fas', 'book'],
          route: {
            name: 'AdditionalResources'
          },
          children: [
            {
              subMenuText: 'Support Portal',
              href: 'https://support.ringbax.com',
              target: '_blank'
            }
          ]
        },
        {
          menuText: 'Ringba Integration',
          menuIcon: ['fas', 'link'],
          route: {
            name: 'RingbaIntegration'
          },
          condition: () => this.isSellerAccount && isRingbaIntegrationEnabled()
        },
        {
          menuText: 'Logout',
          menuIcon: ['fas', 'power-off'],
          href: 'javascript:;',
          route: {
            name: 'Logout'
          },
          click: this.logoutAndRedirect
        }
      ]
    }
  },  

  computed: {
    ...mapState(['isSidebarExpanded']),    

    ...mapGetters('auth', [
      'isBuyerAccount',
      'isSellerAccount',
      'accountDetails',
      'hasAllowedMedicareCategory',
      'isSuperAdmin'
    ]),

    sidebarClass() {
      let styleClasses = 'd-md-flex'
      return this.isSidebarExpanded
        ? styleClasses
        : `${styleClasses} sidebar--collapsed`
    }
  },  

  methods: {    
    noop() {}    
  }
}
</script>
